/**
 * Dokumenten-Upload einer Bestellung - `POST /api/orders/:orderId/documents/:docType`
 * (`backend/src/http/routes/documents.ts:217`), als `multipart/form-data`. Parameternamen von der
 * Route abgelesen: Pfad traegt `:orderId`/`:docType` (NICHT `:id`), der Formularkoerper `rowVersion`/
 * `note`/`ciNummer` als Textfelder (`leseMultipartKoerper`, `documents.ts:108-159`) - `graphName`
 * bleibt hier ungenutzt (kein Aufrufer setzt es). Der Feldname der Datei selbst ist beliebig: busboy
 * liest den ERSTEN Dateistrom, unabhaengig vom Feldnamen (`documents.ts:161`, `_feldname`
 * ungelesen).
 *
 * Groessengrenze des Handuploads (100 MB, `lib/upload.ts#istZuGross`): NICHT hier dupliziert - die
 * Aufrufer pruefen vorab (`DokumenteTab.tsx#dateienGewaehlt` u. a.). Ein zu grosser Upload liefert
 * 413 und wirft ueber `ladeHoch()` wie jeder andere Fehlstatus; `lib/upload.ts#uploadFehlerText`
 * macht daraus den Text. Alle Upload-Funktionen dieser Datei senden ueber `basis.ts#ladeHoch`
 * (XMLHttpRequest, wegen des Sendefortschritts) und nehmen als letzten, optionalen Parameter einen
 * `fortschritt`-Rueckruf.
 *
 * `onHochladen` (`DokumenteTabProps.onHochladen`, `DokumenteTab.tsx`) reicht `rowVersion` seit dem
 * Fix zu "Ueberschreiben scheitert immer mit 409" (07.09.2026) mit herein - `ciNummer` bleibt
 * weiterhin unbenutzt; ein spaeterer Aufrufer damit kann es ergaenzen, ohne die Signatur zu aendern
 * (optionaler Parameter). BEWUSST OHNE feste Zeilenangabe (anders als sonst in diesem Kommentarblock):
 * der Prop-Docblock ist selbst schon so lang, dass er bei der naechsten Ergaenzung verschiebt - ein
 * Symbolverweis veraltet dabei nicht.
 *
 * ================= Dazu: die Dokumenten-Checkliste (Auftrag "Dokumente manuell abhaken") =========
 *
 * `checklisteLaden`/`checklisteEintragAendern` unten sprechen den SEPARATEN 4a-Router
 * (`backend/src/http/routes/orderDocuments4a.ts`) an - `order_documents.status`, nicht `pdfs[]`. Sie
 * ersetzen den vorherigen (nie funktionierenden) Weg ueber `docsManual`/`docsNa`
 * (`api/bestellungen.ts#KEIN_FELDGRUPPEN_WEG`, `NICHT_SCHREIBBAR`): ein Schreibversuch dorthin warf
 * synchron `FeldOhneGruppeFehler`, VOR jeder Netzwerkanfrage - der Klick auf "manuell abhaken"/"Nicht
 * notwendig" endete deshalb IMMER im generischen Toast "Die Bestellung konnte nicht geaendert
 * werden.", ohne dass je ein Request rausging. `DokumenteTab.tsx` ruft beide Funktionen direkt auf
 * (lokaler State, kein Umweg ueber `App.tsx#drawerAendern`) - anders als `dokumentHochladen` oben,
 * das ueber `onHochladen`/App.tsx laeuft.
 */
import { anfrage, anfrageOderNull, ladeHoch } from "./basis.ts";
import type { ChecklistenEintrag, DokumentChecklistenZeile, OrderDocument } from "../lib/types.ts";
import type { Reichweite } from "../lib/dokumentReichweite.ts";

/**
 * Antwortform von `POST .../documents/:docType` (`documents.ts:337-341`), oeffentlich (camelCase).
 *
 * `hinweis` (Sammelsendung, Task 4b): seit dieser Aufgabe Teil der Antwort, `null` im Regelfall.
 * Gesetzt, wenn ein sendungsweiter Typ (Vorbelegung ODER explizite Wahl `reichweite: "sendung"`)
 * hochgeladen wird, OHNE dass die Bestellung an einer Sendung haengt - kein Fehler (der Upload faellt
 * auf den bestellbezogenen Weg zurueck), aber `App.tsx#drawerDokumentHochladen` zeigt ihn als Toast,
 * damit der Hochladende erfaehrt, dass das Dokument NUR hier liegt. Optional statt `string | null`,
 * damit ein aeltere Aufrufer-Attrappe (z. B. eine Testfixtur ohne dieses Feld) weiter durchgeht.
 */
export interface DokumentHochgeladenAntwort {
  document: OrderDocument;
  version: Record<string, unknown>;
  file: Record<string, unknown>;
  hinweis?: string | null;
}

/** Eine aktive Standarddatei oder ein automatisch gelesener DSV-Beleg aus der gemeinsamen Anzeige. */
export interface DokumentAnsicht {
  id: string;
  orderId: string;
  docType: string | null;
  status: "offen" | "vorhanden" | "entfaellt";
  currentVersion: number | null;
  rowVersion: number;
  updatedAt: string;
  updatedBy: string | null;
  viaGroupId: string | null;
  viaShipmentId: string | null;
  /** Sichtbare DSV-Sendungsnummer der Herkunft; keine interne UUID im Drawer. */
  viaShipmentNo?: string | null;
  /** Anzahl der Bestellungen mit demselben sendungsweiten Dokument. */
  geteiltMit?: number | null;
  groupStatus: "active" | "dissolved" | null;
  groupLabel: string | null;
  fileId: string | null;
  originalName: string;
  source: "manual" | "sharepoint_import" | "dsv";
  downloadPath: string | null;
  editable: boolean;
  /**
   * Herkunft der Verknuepfung (Spec „Dokument entfernen“): bestimmt, ob und wie das Entfernen nach der
   * Reichweite fragt. Fehlt das Feld (aeltere Antwort/Testattrappe), leitet `DokumenteTab.tsx` sie ab.
   */
  herkunft?: DokumentHerkunft;
  /** Zahl der Bestellungen, die „Alle“ beim Entfernen traefe; 1 bei eigen/behalten. */
  reichweiteAnzahl?: number;
}

/** Wie die Verknuepfung an der Bestellung haengt (`document_link_removals.herkunft`). */
export type DokumentHerkunft = "eigen" | "behalten" | "sendung" | "gruppe" | "sammelsendung";

/** Lädt alle im Dokumentreiter sichtbaren Standard- und DSV-Dokumente einer Bestellung. */
export function dokumenteLaden(orderId: string, signal?: AbortSignal): Promise<DokumentAnsicht[]> {
  return anfrage<DokumentAnsicht[]>(`/api/orders/${encodeURIComponent(orderId)}/documents`, { signal });
}

/** Die beiden Dokumenttypen, die ausschliesslich an einer JTL-Bestellposition liegen. */
export type PositionsDokumenttyp = "artwork" | "produktionsbilder";

/** Aktuelle Fassung eines positionsbezogenen Dokuments aus der separaten Uebersichtsroute. */
export interface PositionsDokument {
  linkId: string;
  jtlPosId: number;
  docType: PositionsDokumenttyp;
  documentId: string;
  fileId: string;
  rowVersion: number;
  version: number;
  originalName: string;
  /** MIME-Typ der aktuellen Fassung; `null`, wenn die Datei keinen traegt. */
  contentType: string | null;
  /** Sortierindex der JTL-Position, an der das Dokument haengt. */
  positionSortIndex: number;
  /** Zeitpunkt der letzten Sortier-Aenderung an diesem Bild. */
  bildSortUpdatedAt: string;
}

/**
 * Ein Positionsdokument einer ANDEREN Bestellung, das an einer Position mit gleicher Artikelnummer uebernommen werden kann
 * (Positionsersatz C2, `GET .../position-document-suggestions`). `documentId` ist die id fuer `positionsDokumentVerknuepfen`;
 * `bestellnummer` ist die JTL-Bestellnummer der Quelle (sonst deren Bestell-ID), `stand` der Zeitpunkt der aktuellen Fassung.
 */
export interface PositionsDokumentVorschlag {
  linkId: string;
  documentId: string;
  fileId: string;
  docType: PositionsDokumenttyp;
  originalName: string;
  artikelNummer: string;
  orderId: string;
  bestellnummer: string;
  jtlPosId: number;
  stand: string;
}

/** Eine Fassung aus der Historie eines positionsbezogenen Dokuments. */
export interface PositionsDokumentVersion {
  version: number;
  fileId: string;
  originalName: string;
  note: string | null;
  replacedBy: number | null;
  actorId: string | null;
  actor: string | null;
  ts: string;
}

/** Die Verknuepfungszeile einer positionsbezogenen Fassung. */
export interface PositionsDokumentLink {
  id: string;
  orderId: string;
  docType: PositionsDokumenttyp;
  status: "offen" | "vorhanden" | "entfaellt";
  currentVersion: number | null;
  rowVersion: number;
  updatedAt: string;
  updatedBy: string | null;
}

/** Antwort des Positions-Uploads; sie enthält zusätzlich die neu angelegte Datei. */
export interface PositionsDokumentHochgeladenAntwort {
  document: PositionsDokumentLink;
  version: Omit<PositionsDokumentVersion, "originalName">;
  file: Record<string, unknown>;
}

/** Antwort der Hash-Uebernahme: Route liefert `dokument`, nicht `document`. */
export interface PositionsDokumentVerknuepfungAntwort {
  dokument: PositionsDokumentLink;
  version: Omit<PositionsDokumentVersion, "originalName">;
}

/** Ein auch soft-geloeschtes Produktionsbild mit seiner vollstaendigen Fassungskette. */
export interface ProduktionsbildHistorienElement {
  linkId: string;
  rowVersion: number;
  jtlPosId: number;
  originalName: string;
  deletedAt: string | null;
  deletedBy: string | null;
  versionen: PositionsDokumentVersion[];
  ereignisse: Array<{
    neuWert?: string | null;
    aktor?: string;
    ts: string;
  }>;
}

function positionsDokumentPfad(orderId: string, jtlPosId: number, docType: PositionsDokumenttyp): string {
  return `/api/orders/${encodeURIComponent(orderId)}/positions/${jtlPosId}/documents/${encodeURIComponent(docType)}`;
}

/**
 * Die Vorschauvarianten der Task-2-Route. Bewusst ein Union und kein `string`: `"original"`
 * liefert die Route nicht aus, ein offener Parameter wuerde es aber annehmen.
 */
export type ProduktionsbildPreviewVariante = "thumbnail" | "lightbox";

/**
 * Pfad der Vorschauroute eines Produktionsbilds. `orderId` und `linkId` sind Text und gehen kodiert
 * mit; `jtlPosId` ist eine Zahl und bleibt roh.
 */
export function produktionsbildPreviewPfad(
  orderId: string,
  jtlPosId: number,
  linkId: string,
  variante: ProduktionsbildPreviewVariante,
  fileId: string,
): string {
  const pfad = `${positionsDokumentPfad(orderId, jtlPosId, "produktionsbilder")}/${encodeURIComponent(linkId)}/preview/${variante}`;
  return `${pfad}?fileId=${encodeURIComponent(fileId)}`;
}

/** Laedt nur die aktuelle Fassung der positionsbezogenen Dokumente einer Bestellung. */
export function positionsDokumenteLaden(orderId: string, signal?: AbortSignal): Promise<PositionsDokument[]> {
  return anfrage<PositionsDokument[]>(
    `/api/orders/${encodeURIComponent(orderId)}/position-documents`,
    { signal },
  );
}

/**
 * Laedt die Positionsdokumente ANDERER Bestellungen, deren Position denselben Artikel fuehrt wie eine der eigenen (Vorschlaege fuer
 * „Vorhandenes Positionsdokument uebernehmen“). Das Backend blendet aus, was die eigene Auswahl schon zeigt, und begrenzt je Artikel
 * und Typ; ein Aufrufer filtert nicht nach.
 */
export function positionsDokumentVorschlaegeLaden(orderId: string, signal?: AbortSignal): Promise<PositionsDokumentVorschlag[]> {
  return anfrage<PositionsDokumentVorschlag[]>(
    `/api/orders/${encodeURIComponent(orderId)}/position-document-suggestions`,
    { signal },
  );
}

/**
 * Legt eine Fassung von Artwork oder Produktionsbildern an einer JTL-Position ab. `fortschritt`
 * (letzter, optionaler Parameter, gilt fuer alle Upload-Funktionen dieser Datei): meldet den
 * Sendeanteil 0 bis 1, nach der erfolgreichen Antwort einmal 1 (`basis.ts#ladeHoch`).
 */
export function positionsDokumentHochladen(
  orderId: string,
  jtlPosId: number,
  docType: PositionsDokumenttyp,
  datei: File,
  rowVersion?: number | null,
  notiz?: string,
  fortschritt?: (anteil: number) => void,
): Promise<PositionsDokumentHochgeladenAntwort> {
  const formular = new FormData();
  formular.append("file", datei);
  if (rowVersion !== undefined && rowVersion !== null) {
    formular.append("rowVersion", String(rowVersion));
  }
  if (notiz) formular.append("note", notiz);
  return ladeHoch<PositionsDokumentHochgeladenAntwort>(
    positionsDokumentPfad(orderId, jtlPosId, docType),
    formular,
    { onFortschritt: fortschritt },
  );
}

/** Fuegt der Galerie ein neues, von allen anderen Bildern unabhaengiges Element hinzu. */
export function produktionsbildHochladen(
  orderId: string,
  jtlPosId: number,
  datei: File,
  notiz?: string,
  fortschritt?: (anteil: number) => void,
): Promise<PositionsDokumentHochgeladenAntwort> {
  return positionsDokumentHochladen(
    orderId,
    jtlPosId,
    "produktionsbilder",
    datei,
    null,
    notiz,
    fortschritt,
  );
}

function produktionsbildPfad(orderId: string, jtlPosId: number, linkId: string): string {
  return `${positionsDokumentPfad(orderId, jtlPosId, "produktionsbilder")}/${encodeURIComponent(linkId)}`;
}

/** Ersetzt ausschliesslich die aktuelle Fassung des mit `linkId` adressierten Galeriebilds. */
export function produktionsbildErsetzen(
  orderId: string,
  jtlPosId: number,
  linkId: string,
  datei: File,
  rowVersion: number,
  begruendung: string,
  fortschritt?: (anteil: number) => void,
): Promise<PositionsDokumentHochgeladenAntwort> {
  const formular = new FormData();
  formular.append("file", datei);
  formular.append("rowVersion", String(rowVersion));
  formular.append("note", begruendung);
  return ladeHoch<PositionsDokumentHochgeladenAntwort>(
    `${produktionsbildPfad(orderId, jtlPosId, linkId)}/versions`,
    formular,
    { onFortschritt: fortschritt },
  );
}

/** Blendet genau das mit `linkId` adressierte Bild aus der aktiven Galerie aus. */
export function produktionsbildLoeschen(
  orderId: string,
  jtlPosId: number,
  linkId: string,
  rowVersion: number,
): Promise<PositionsDokumentVerknuepfungAntwort> {
  return anfrage<PositionsDokumentVerknuepfungAntwort>(produktionsbildPfad(orderId, jtlPosId, linkId), {
    methode: "DELETE",
    koerper: { rowVersion },
  });
}

/** Stellt ein soft-geloeschtes Galeriebild mit seiner aktuellen Row-Version wieder her. */
export function produktionsbildWiederherstellen(
  orderId: string,
  jtlPosId: number,
  linkId: string,
  rowVersion: number,
): Promise<PositionsDokumentVerknuepfungAntwort> {
  return anfrage<PositionsDokumentVerknuepfungAntwort>(`${produktionsbildPfad(orderId, jtlPosId, linkId)}/restore`, {
    methode: "POST",
    koerper: { rowVersion },
  });
}

/** Liefert aktive und geloeschte Produktionsbilder der gewaehlten Position samt Versionen. */
export function produktionsbildHistorieLaden(
  orderId: string,
  jtlPosId: number,
  signal?: AbortSignal,
): Promise<ProduktionsbildHistorienElement[]> {
  return anfrage<ProduktionsbildHistorienElement[]>(
    `${positionsDokumentPfad(orderId, jtlPosId, "produktionsbilder")}/history`,
    { signal },
  );
}

/** Laedt die vollstaendige, ausschliesslich einer Position gehoerende Fassungskette. */
export function positionsDokumentVersionenLaden(
  orderId: string,
  jtlPosId: number,
  docType: PositionsDokumenttyp,
  signal?: AbortSignal,
): Promise<PositionsDokumentVersion[]> {
  return anfrage<PositionsDokumentVersion[]>(
    `${positionsDokumentPfad(orderId, jtlPosId, docType)}/versions`,
    { signal },
  );
}

/** Uebernimmt die aktuelle Datei eines Hash-Treffers als eigenstaendige Positionskette. */
export function positionsDokumentVerknuepfen(
  orderId: string,
  jtlPosId: number,
  docType: PositionsDokumenttyp,
  documentId: string,
): Promise<PositionsDokumentVerknuepfungAntwort> {
  return anfrage<PositionsDokumentVerknuepfungAntwort>(
    `${positionsDokumentPfad(orderId, jtlPosId, docType)}/link`,
    { methode: "POST", koerper: { documentId } },
  );
}

/**
 * `POST /api/orders/:orderId/documents/:docType`. `notiz` geht als Textfeld `note` mit (nur bei
 * einer Ueberschreibung gesetzt, siehe `DokumenteTab.tsx#ueberschreibenBestaetigen`).
 *
 * `rowVersion` (Fix "Ueberschreiben scheitert immer mit 409", 07.09.2026): geht als Textfeld
 * `rowVersion` mit, wenn gesetzt (weder `undefined` noch `null`) - genau der Fall einer bereits
 * vorhandenen `document_links`-Zeile. `legeVersionAn` (`backend/src/documents/repository.ts`) wirft
 * fuer eine solche Zeile IMMER einen `VersionConflictError` (409), wenn `rowVersion` fehlt - vor
 * diesem Fix schickte diese Funktion es NIE mit, der Ueberschreib-Dialog konnte strukturell nie
 * erfolgreich sein. Bei einer Erstablage (keine vorhandene Zeile) bleibt der Parameter `null`/
 * `undefined`, wie bisher.
 *
 * `reichweite` (Sammelsendung, Task 4b, MERGE 07.09.2026): die vom Hochladenden getroffene Wahl aus
 * `ReichweiteAuswahl.tsx` - optional wie `notiz`, geht nur mit, wenn der Aufrufer sie mitgibt (siehe
 * `DokumenteTab.tsx#dateienGewaehlt`: ohne verknuepfte Sendung gibt es keine Wahl zu treffen, das
 * Feld bleibt dann weg statt `undefined` explizit zu senden). Steht als SECHSTER Parameter, NACH
 * `rowVersion` - die beiden schliessen sich gegenseitig aus (eine Ueberschreibung fragt nie nach der
 * Reichweite, siehe `DokumenteTab.tsx`, Fixrunde 1 Befund 3).
 */
export function dokumentHochladen(
  orderId: string,
  docType: string,
  datei: File,
  notiz?: string,
  rowVersion?: number | null,
  reichweite?: Reichweite,
  fortschritt?: (anteil: number) => void,
): Promise<DokumentHochgeladenAntwort> {
  const formular = new FormData();
  formular.append("file", datei);
  if (notiz) formular.append("note", notiz);
  if (rowVersion !== undefined && rowVersion !== null) {
    formular.append("rowVersion", String(rowVersion));
  }
  if (reichweite) formular.append("reichweite", reichweite);
  return ladeHoch<DokumentHochgeladenAntwort>(
    `/api/orders/${encodeURIComponent(orderId)}/documents/${encodeURIComponent(docType)}`,
    formular,
    { onFortschritt: fortschritt },
  );
}

/**
 * Antwortform von `GET /api/orders/:id/document-checklist`
 * (`backend/src/domaene/checkliste.ts#checklisteFuer`).
 */
export interface ChecklisteAntwort {
  eintraege: ChecklistenEintrag[];
  offenePflicht: number;
}

/**
 * `GET /api/orders/:id/document-checklist` - alle 22 Checklisteneintraege einer Bestellung. Lesen
 * steht allen Rollen offen (Constraint 9, `endpunktMatrix.ts`), keine Berechtigungspruefung noetig.
 * `signal` wie bei `einbuchungenLaden` (`api/einbuchungen.ts`) - der Aufrufer bricht beim Wechsel der
 * Bestellung ab, statt eine veraltete Antwort in den State zu schreiben.
 */
export function checklisteLaden(orderId: string, signal?: AbortSignal): Promise<ChecklisteAntwort> {
  return anfrage<ChecklisteAntwort>(
    `/api/orders/${encodeURIComponent(orderId)}/document-checklist`,
    { signal },
  );
}

/**
 * `PATCH /api/orders/:id/documents/:docType` - setzt `order_documents.status` auf `"offen"` oder
 * `"entfaellt"` (`"vorhanden"` ist hier serverseitig gesperrt, siehe Backend-Kommentar an
 * `STATUS_UNGUELTIG_MELDUNG`). `rowVersion` ist `null` bei der Erstanlage der Checklistenzeile
 * (`ChecklistenEintrag.rowVersion`, wenn noch keine `order_documents`-Zeile existiert), sonst die
 * zuletzt gelesene Zahl - Optimistic Concurrency, 409 (`KonfliktFehler`) bei einem zwischenzeitlich
 * fremd geaenderten Stand.
 */
export function checklisteEintragAendern(
  orderId: string,
  docType: string,
  status: "offen" | "entfaellt",
  rowVersion: number | null,
): Promise<DokumentChecklistenZeile> {
  return anfrage<DokumentChecklistenZeile>(
    `/api/orders/${encodeURIComponent(orderId)}/documents/${encodeURIComponent(docType)}`,
    { methode: "PATCH", koerper: { rowVersion, status } },
  );
}

/**
 * ================= Duplikaterkennung per SHA-256 (Auftrag "Duplikaterkennung beim
 * Dokumenten-Upload per SHA-256", 07.09.2026) =================
 *
 * Antwortform von `GET /api/documents/by-hash/:sha256` (`backend/src/http/routes/documentsHash.ts`,
 * `backend/src/documents/repository.ts#findeDokumentPerHash`) - GLOBAL, nicht unter einer
 * Bestellung gemountet (anders als jede andere Funktion dieser Datei): derselbe Hash kann an
 * mehreren Bestellungen/Dokumenttypen haengen, `links` traegt deshalb ALLE Fundstellen.
 *
 * `hochgeladenAm` (Nachbesserung, Punkt 7, 07.09.2026): der tatsaechliche Upload-/Erstellungs-
 * zeitpunkt der Datei - EIN Zeitpunkt fuer den gesamten Treffer (Eigenschaft der Datei, nicht einer
 * einzelnen Verknuepfung). Vorher stand hier je `links[]`-Eintrag ein `uploadedAt`, das aber
 * `document_links.updated_at` trug (Zeitpunkt des letzten Status-Wechsels DIESER Verknuepfung) -
 * fachlich falsch benannt und fuer die Info-Box irrefuehrend, siehe `repository.ts`.
 *
 * `orderKennung` je Link (Nachbesserung, Punkt 6): eine lesbare Ersatzkennung (`jtl-<n>`, oder die
 * rohe `orderId` ohne JTL-Bezug) fuer den Fall, dass die Bestellung im Frontend gerade nicht geladen
 * ist (Archiv, anderer Filter) - siehe `DokumenteTab.tsx#linkBeschriftung`.
 */
export interface DokumentPerHashTreffer {
  documentId: string;
  originalName: string;
  hochgeladenAm: string;
  links: { orderId: string; docType: string; orderKennung: string }[];
}

/**
 * `GET /api/documents/by-hash/:sha256` - liefert `null` bei 404 (kein Treffer), statt zu werfen
 * (`anfrageOderNull`, wie `ressource().einzeln`). `DokumenteTab.tsx#dateiGewaehlt` ruft dies VOR
 * jedem Upload mit dem client-seitig berechneten Hash der gewaehlten Datei auf.
 */
export function dokumentPerHashLaden(
  sha256: string,
  signal?: AbortSignal,
): Promise<DokumentPerHashTreffer | null> {
  return anfrageOderNull<DokumentPerHashTreffer>(`/api/documents/by-hash/${encodeURIComponent(sha256)}`, {
    signal,
  });
}

/**
 * `POST /api/orders/:orderId/documents/:docType/link` - verknuepft ein BEREITS vorhandenes Dokument
 * (`documentId` aus einem `dokumentPerHashLaden`-Treffer) mit dieser Bestellung/diesem Dokumenttyp.
 * Schreibt/laedt keine Datei; Gegenstueck zu `dokumentHochladen` fuer den Fall, dass der Nutzer den
 * Duplikat-Dialog (`DuplikatDialog.tsx`) mit "Verknüpfen" bestaetigt.
 */
export function dokumentVerknuepfen(
  orderId: string,
  docType: string,
  documentId: string,
): Promise<DokumentChecklistenZeile> {
  return anfrage<DokumentChecklistenZeile>(
    `/api/orders/${encodeURIComponent(orderId)}/documents/${encodeURIComponent(docType)}/link`,
    { methode: "POST", koerper: { documentId } },
  );
}

/**
 * Reichweite beim Entfernen: nur die angeklickte Bestellung oder alle, die das Dokument ueber Sendung,
 * Bestellgruppe oder Sammelsendung teilen. NICHT dasselbe wie die Upload-`Reichweite`
 * (`lib/dokumentReichweite.ts`: bestellung/gruppe/sendung) - der Server entscheidet beim Entfernen
 * anhand der Herkunft der Zeile, was „alle“ umfasst.
 */
export type EntfernReichweite = "bestellung" | "alle";

/** Antwort von `DELETE /api/orders/:orderId/document-links/:linkId`. */
export interface DokumentEntferntAntwort {
  entfernt: { orderId: string; linkId: string; docType: string }[];
  bestellungen: number;
}

/**
 * `DELETE /api/orders/:orderId/document-links/:linkId` - entfernt die Verknuepfung eines Dokuments mit
 * der Bestellung (Datei und Fassungen bleiben, das Entfernen wird protokolliert, siehe
 * `entfernteDokumenteLaden`). `rowVersion` ist die der angeklickten Zeile (409 bei Abweichung),
 * `reichweite` gilt nur bei geerbten Dokumenten (eigene: der Server lehnt `alle` mit 422 ab). Rechte
 * wie beim Upload (`manage_documents`, bei Zahlungsbelegtypen auch `manage_payments`).
 *
 * Seit Ticket F auch fuer das ARTWORK einer Position (`linkId` aus `positionsDokumenteLaden`, `reichweite`
 * `"bestellung"`); Produktionsbilder und die uebrigen Positionstypen lehnt der Server mit 422 ab - sie gehen weiter ueber
 * `produktionsbildLoeschen`.
 */
export function dokumentEntfernen(
  orderId: string,
  linkId: string,
  rowVersion: number,
  reichweite: EntfernReichweite,
): Promise<DokumentEntferntAntwort> {
  return anfrage<DokumentEntferntAntwort>(
    `/api/orders/${encodeURIComponent(orderId)}/document-links/${encodeURIComponent(linkId)}`,
    { methode: "DELETE", koerper: { rowVersion, reichweite } },
  );
}

/**
 * `POST /api/orders/:orderId/document-links/:linkId/umhaengen` - haengt ein Positionsdokument (Artwork oder Produktionsbild) an
 * eine andere, aktuelle Position derselben Bestellung (Positionsersatz C1, fuer Dokumente ohne Position). `zielJtlPosId` ist die
 * JTL-Positions-Id des Ziels, `rowVersion` die der angeklickten Zeile. Die Antwort nennt die Position, an der das Dokument jetzt
 * haengt, und die neue `rowVersion`. Recht wie das Entfernen: `manage_documents`.
 *
 * Fehler: 409 mit `aktuellerStand` = die Zeile hat sich inzwischen geaendert (neu laden); 409 OHNE `aktuellerStand` = das Ziel ist
 * schon belegt (nur im Wettlauf, die Oberflaeche sperrt solche Ziele), der Servertext sagt welches; 422 = das Ziel ist keine
 * lebende Position der Bestellung mehr; 404 = Bestellung oder Verknuepfung fehlt.
 */
export function positionsDokumentUmhaengen(
  orderId: string,
  linkId: string,
  zielJtlPosId: number,
  rowVersion: number,
): Promise<{ linkId: string; jtlPosId: number; rowVersion: number }> {
  return anfrage<{ linkId: string; jtlPosId: number; rowVersion: number }>(
    `/api/orders/${encodeURIComponent(orderId)}/document-links/${encodeURIComponent(linkId)}/umhaengen`,
    { methode: "POST", koerper: { zielJtlPosId, rowVersion } },
  );
}

/**
 * `POST /api/orders/:orderId/dsv-belege/:belegId/zuordnung` - ordnet einen automatisch gelesenen
 * DSV-Beleg per Klick einer Dokumentkategorie der Bestellung zu (Spec 2026-10-17). Antwort bei Erfolg
 * 201 `{ document, version, hinweis }`.
 *
 * Fehlerbehandlung wie `dokumentHochladen`: `anfrage` wirft bei 409 einen `KonfliktFehler`. Ist der
 * Platz der Kategorie belegt, traegt `aktuellerStand` das bestehende Dokument samt `rowVersion` -
 * der Aufrufer fragt dann nach und wiederholt den Aufruf mit dieser `rowVersion`. Ohne `rowVersion` im
 * Koerper (Erstzuordnung) bleibt das Feld weg, statt `undefined` zu senden.
 */
export async function dsvBelegZuordnen(
  bestellungId: string,
  belegId: string,
  eingabe: { docType: string; reichweite: "bestellung" | "sendung"; rowVersion?: number },
): Promise<unknown> {
  const { docType, reichweite, rowVersion } = eingabe;
  return anfrage<unknown>(
    `/api/orders/${encodeURIComponent(bestellungId)}/dsv-belege/${encodeURIComponent(belegId)}/zuordnung`,
    {
      methode: "POST",
      koerper: { docType, reichweite, ...(rowVersion !== undefined ? { rowVersion } : {}) },
    },
  );
}

/** Ein Eintrag aus `GET /api/orders/:orderId/document-removals` (neueste zuerst). */
export interface EntferntesDokument {
  id: string;
  docType: string;
  originalName: string;
  removedAt: string;
  removedBy: { id: string; name: string } | null;
  herkunft: DokumentHerkunft;
  reichweite: EntfernReichweite;
  shipmentNo: string | null;
  groupLabel: string | null;
  /** Wie `DokumentAnsicht.downloadPath` - Abruf mit Bearer, siehe `useDateiDownload`. */
  downloadPath: string | null;
  /**
   * Nur bei Positionsdokumenten (Artwork `docType: "artwork"`, Produktionsbild `docType: "produktionsbilder"`): die
   * JTL-Positions-Id (kLieferantenBestellungPos) - NICHT die laufende Nummer der Position (die ergibt sich aus den
   * Positionen der Bestellung, `lib/positionsAnzeige.ts#entferntesDokumentPosition`). Bei Bestelldokumenten `null`.
   */
  jtlPosId: number | null;
  /** Artikelnummer der Position, lokal aus dem Beobachtungsstand; `null`, wenn unbekannt oder kein Positionsdokument. */
  artikelNummer: string | null;
}

/** Lädt die Liste der von dieser Bestellung entfernten Dokumente (Recht wie die Belegliste). */
export function entfernteDokumenteLaden(orderId: string, signal?: AbortSignal): Promise<EntferntesDokument[]> {
  return anfrage<EntferntesDokument[]>(`/api/orders/${encodeURIComponent(orderId)}/document-removals`, { signal });
}
